/**
 * Endless mode: the same event grammar, generated (plan A1, M10).
 *
 * Reusing `ShiftScript` rather than inventing a second format matters. Every invariant the
 * campaign scripts are held to — bearings inside the open corridor, fights staged inside
 * the gun's envelope, waves gated on the protected ship's range, generous timeouts — is
 * enforced by `validateShift`, and a generator that emits the same structure inherits all
 * of it. A bespoke endless system would have had to re-earn each of those lessons.
 *
 * The ramp is deliberately not linear. Watch 1 of Endless is roughly a shift-6 evening;
 * by watch 10 it is past anything the campaign asks for. What grows is the number of
 * simultaneous threats and how early they arrive, not the toughness of any one hull —
 * scaling HP would only make the same fight longer, whereas scaling pressure changes what
 * the player has to decide.
 */

import type { ShiftScript, ShiftBeat, SpawnBeat } from '../schema';
import { ENDLESS_OPENING } from './openings';
import { ENDLESS_LINES } from '../radio';
import type { VesselKind } from '../../sim/types';
import { Rng } from '../../sim/rng';
import { APPROACH_ARC, isBearingMasked } from '../schema';

/** Threat types unlocked as the watch number climbs. */
const LADDER: ReadonlyArray<{ from: number; kind: VesselKind; weight: number }> = [
  { from: 1, kind: 'skiff', weight: 4 },
  { from: 1, kind: 'gunboat', weight: 2 },
  { from: 2, kind: 'fac', weight: 2 },
  { from: 4, kind: 'minisub', weight: 2 },
  { from: 6, kind: 'corvette', weight: 1 },
  { from: 8, kind: 'tentacle', weight: 2 },
];

const WEATHERS = ['overcastDay', 'dusk', 'night', 'fogBank', 'storm', 'nightStorm'] as const;

/**
 * What the player is actually carrying, for the substitution pass below.
 *
 * Optional everywhere: without it a watch generates exactly as it always did, which is
 * what the validation tests want.
 */
export interface EndlessLoadout {
  torpedoes: number;
  depthCharges: number;
}

/**
 * The closest a wave may be gated, kilometres.
 *
 * The shrinking gate (`2.8 - w * 0.12`) is how a late watch buys less warning, and left
 * alone it crosses the Safe Line around watch 16 — spawning a wave to attack a hull that
 * has already become untouchable, which reads as hostiles milling about for no reason.
 * The escalation that gate was buying transfers to wave size and overlap, which scale
 * without bound.
 */
const GATE_FLOOR_KM = 1.3;

/**
 * Pick a bearing inside the open corridor, leaving room for the group's fan.
 *
 * `side` splits the corridor so consecutive waves can be pushed to opposite edges: the
 * gun's real cost is traverse time, and two waves sixty degrees apart is a completely
 * different problem from two waves on the same bearing, at no extra content.
 */
function bearingFor(rng: Rng, count: number, spreadDeg: number, side: 'port' | 'starboard'): number {
  const halfFan = ((count - 1) / 2) * spreadDeg;
  const lo = APPROACH_ARC.min + halfFan;
  const hi = APPROACH_ARC.max - halfFan;
  const mid = (lo + hi) / 2;
  const [min, max] = side === 'port' ? [lo, mid] : [mid, hi];
  return min + rng.next() * Math.max(0, max - min);
}

/**
 * Swap out any hull the player has no answer to.
 *
 * The recorded balance finding: watch 6 unlocks the corvette, the autocannon does 25%
 * against her armour, and the generator had no idea what was in the magazine — so a player
 * who arrived without torpedoes met a hull they simply could not hurt. Mini-subs are the
 * same problem one weapon along.
 *
 * Substituting *after* generation rather than seeding the loadout into it is deliberate:
 * watch 9 stays watch 9 for everyone — same seed, same skeleton, same score meaning — it
 * just never fields something unanswerable. Bearings are re-clamped because a single hull
 * becoming a pair needs room for its fan, and `validateShift` is unforgiving about that.
 */
function substituteUnanswerable(beats: ShiftBeat[], loadout: EndlessLoadout): void {
  for (const beat of beats) {
    if (beat.kind !== 'spawn') continue;
    const b = beat as SpawnBeat & { spreadDeg?: number };

    if (b.vessel === 'corvette' && loadout.torpedoes < 1) {
      b.vessel = 'fac';
      b.count = 2;
      b.spreadDeg = 16;
      b.rangeKm = Math.min(b.rangeKm, 2.3);
    } else if (b.vessel === 'minisub' && loadout.depthCharges < 2) {
      b.vessel = 'gunboat';
      b.count = 2;
      b.spreadDeg = 16;
      b.rangeKm = Math.min(b.rangeKm, 2.2);
    } else {
      continue;
    }

    const halfFan = ((b.count - 1) / 2) * (b.spreadDeg ?? 0);
    const lo = APPROACH_ARC.min + halfFan;
    const hi = APPROACH_ARC.max - halfFan;
    b.bearingDeg = Math.min(hi, Math.max(lo, b.bearingDeg));
    // Belt and braces: the corridor is contiguous, but a fan that still straddles a masked
    // sector gets nudged rather than shipped, because validateShift would reject it.
    for (let n = 0; n < b.count; n++) {
      const offset = (n - (b.count - 1) / 2) * (b.spreadDeg ?? 0);
      if (isBearingMasked(b.bearingDeg + offset)) b.bearingDeg = (lo + hi) / 2;
    }
  }
}

/**
 * Build one Endless watch.
 *
 * Seeded from the watch number, so a given watch is the same fight for every player and
 * a high score means something.
 */
export function generateEndlessShift(watch: number, loadout?: EndlessLoadout): ShiftScript {
  const rng = new Rng(`endless-${watch}`);
  const beats: ShiftBeat[] = [];

  const available = LADDER.filter((l) => watch >= l.from);
  const pool: VesselKind[] = [];
  for (const entry of available) {
    for (let i = 0; i < entry.weight; i++) pool.push(entry.kind);
  }

  /*
   * Convoy size grows slowly; the escort burden is what actually scales. Every third
   * watch carries an extra hull with a manifest on it — the recorded finding's other
   * half. Endless is meant to kill the player eventually, but by pressure, not by
   * running them out of ammunition with no way to restock.
   */
  const resupply = watch % 3 === 0;
  const arrivals = Math.min(3, 1 + Math.min(2, Math.floor(watch / 4)) + (resupply ? 1 : 0));
  const waves = 2 + Math.min(4, Math.floor(watch / 2));

  beats.push({ kind: 'objective', trigger: { at: 0 }, text: `Endless watch ${watch}` });
  /*
   * Endless may only ever say a line from `ENDLESS_LINES`. A generated watch cannot
   * invent dialogue, because dialogue has to have been recorded — see content/radio.ts.
   */
  {
    const line = watch < 5 ? ENDLESS_LINES.earlyWatch : ENDLESS_LINES.lateWatch;
    beats.push({
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: line.text, read: line.read,
    });
  }

  /*
   * 5–7 km, and 75 s apart rather than 120.
   *
   * The old 8–10 km spawn bought nothing but voyage: every wave hangs off a hull's range,
   * and she cannot reach any of those gates for minutes, so the distance was silently the
   * watch's pacing rather than its geography. Overlapping the arrivals is also what makes
   * two hulls a *choice* instead of two errands run one after the other.
   */
  for (let i = 0; i < arrivals; i++) {
    const kind: VesselKind = i === 0 ? 'tanker' : 'supply';
    beats.push({
      kind: 'spawn',
      trigger: { at: 5 + i * 75 },
      tag: `friendly-${i}`,
      vessel: kind,
      count: 1,
      rangeKm: 5 + rng.next() * 2,
      bearingDeg: bearingFor(rng, 1, 0, i % 2 === 0 ? 'starboard' : 'port'),
      cargo: i === 0 ? 'oil' : 'mixed',
    });
  }

  /*
   * The opening probe — see ./openings.
   *
   * Every wave below hangs off a convoy hull's range, so without this a watch opened its
   * first fight around 370 s however well the player was shooting. Sized from the ladder
   * like everything else, so a late watch is met by something worth meeting rather than
   * the same two skiffs it saw on watch one.
   */
  {
    const probeKind: VesselKind = watch >= 6 ? 'gunboat' : 'skiff';
    const probeCount = probeKind === 'skiff' ? 1 + Math.min(2, Math.floor(watch / 2)) : 1;
    beats.push({
      kind: 'radio', trigger: { at: ENDLESS_OPENING.radioAt }, speaker: 'RADAR',
      text: ENDLESS_LINES.probe.text, read: ENDLESS_LINES.probe.read,
    });
    beats.push({
      kind: 'spawn',
      trigger: { at: ENDLESS_OPENING.spawnAt },
      tag: 'probe',
      vessel: probeKind,
      count: probeCount,
      rangeKm: ENDLESS_OPENING.rangeKm,
      bearingDeg: bearingFor(rng, probeCount, probeCount > 1 ? ENDLESS_OPENING.spreadDeg : 0, 'port'),
      ...(probeCount > 1 ? { spreadDeg: ENDLESS_OPENING.spreadDeg } : {}),
    });
  }

  for (let w = 0; w < waves; w++) {
    const kind = pool[Math.floor(rng.next() * pool.length)] ?? 'skiff';
    // Big hulls come alone; small craft come in packs that grow with the watch.
    const packable = kind === 'skiff' || kind === 'tentacle';
    const count = packable ? 2 + Math.min(2, Math.floor(watch / 3)) : 1;
    const spread = count > 1 ? 14 : 0;
    const side = w % 2 === 0 ? 'port' : 'starboard';

    /*
     * Alternating gates, alternating sides.
     *
     * Hanging every wave off a hull's range sequences them: each one waits for the convoy
     * to cover the six hundred metres to the next threshold, which is a minute of empty
     * water per wave. Odd waves come for the rig on a plain clock instead, so they land
     * *inside* the stretch a range-gated wave would have left silent.
     */
    const guarding = w % arrivals;
    const trigger = w % 2 === 0
      ? {
        whenWithin: {
          tag: `friendly-${guarding}`,
          km: Math.max(GATE_FLOOR_KM, 2.8 - w * 0.12),
        },
        timeout: 700 + w * 120,
      }
      : { at: 70 + w * 38 };

    // The clock waves really do come for the rig now — the comment above always said
    // so, and the mission flag is what makes the AI agree. Only kinds whose behaviour
    // honours it; a corvette or mini-sub on a clock still fights its own way.
    const rigCapable = kind === 'skiff' || kind === 'gunboat' || kind === 'fac';
    beats.push({
      kind: 'spawn',
      trigger,
      tag: `wave-${w}`,
      vessel: kind,
      count,
      rangeKm: 1.7 + rng.next() * 0.6,
      bearingDeg: bearingFor(rng, count, spread, side),
      ...(spread > 0 ? { spreadDeg: spread } : {}),
      ...(w % 2 === 1 && rigCapable ? { mission: 'rig' as const } : {}),
    });
  }

  /*
   * A last wave, gated tight on the hull that is furthest from home.
   *
   * Whatever the player kills last decides how much empty water sits between that kill
   * and the results screen, so a watch that ends on a wave gated at 2.8 km ends on a
   * minute of nothing. This one lands with the convoy almost in.
   */
  beats.push({
    kind: 'spawn',
    trigger: {
      whenWithin: { tag: `friendly-${arrivals - 1}`, km: GATE_FLOOR_KM },
      timeout: 600,
    },
    tag: 'closer',
    vessel: watch >= 4 ? 'gunboat' : 'skiff',
    count: watch >= 4 ? 1 : 2,
    rangeKm: 1.4,
    bearingDeg: bearingFor(rng, watch >= 4 ? 1 : 2, watch >= 4 ? 0 : 12, 'starboard'),
    ...(watch >= 4 ? {} : { spreadDeg: 12 }),
  });

  if (loadout) substituteUnanswerable(beats, loadout);

  const weather = WEATHERS[Math.floor(rng.next() * WEATHERS.length)] ?? 'overcastDay';

  return {
    index: 100 + watch,
    name: `Endless ${watch}`,
    weather,
    seed: `endless-${watch}`,
    beats,
    // Generous: an Endless watch ends when its arrivals resolve, like every other shift.
    maxDuration: 1800 + waves * 120,
  };
}

/** True when a shift index belongs to Endless rather than the authored campaign. */
export function isEndless(index: number): boolean {
  return index > 100;
}

/** Watch number for an Endless shift index. */
export function endlessWatch(index: number): number {
  return index - 100;
}
